import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink, FiCalendar, FiMapPin } from 'react-icons/fi';
import './styles.css';

/**
 * Modal affichant les événements externes trouvés
 * Permet de transformer un événement en activité Raskmote via "J'y vais"
 */
const LocalEventsModal = ({ 
  events = [], 
  onClose, 
  onBack,
  onCreateActivity,
  sources = [],
  searchQuery = ''
}) => {
  const { t } = useTranslation('common');

  // Mapper un événement externe vers les données CreatePostForm
  const handleCreateActivity = (event) => {
    // Mapper vers le format attendu par CreatePostForm
    const presetData = {
      title: event.title,
      description: `${event.title}${event.sourceName ? `\n\n📍 Source: ${event.sourceName}` : ''}${event.sourceUrl ? `\n🔗 ${event.sourceUrl}` : ''}`,
      category: mapEventCategoryToRaskmote(event.category),
      // Date: essayer de parser si disponible
      date: event.dateStatus === 'KNOWN' && event.dateText ? tryParseDate(event.dateText) : null,
      dateRaw: event.dateText,
      // Location
      location: event.place || event.city ? {
        address: event.place ? `${event.place}, ${event.city}` : event.city,
        city: event.city,
      } : null,
      // Source tracking
      externalSource: {
        name: event.sourceName,
        url: event.sourceUrl,
        id: event.id,
      },
    };

    onCreateActivity(presetData);
  };

  // Badge pour le statut de date
  const renderDateBadge = (event) => {
    if (event.dateStatus === 'UNKNOWN' || !event.dateText) {
      return (
        <span className="local-event-date-badge local-event-date-unknown">
          📅 {t('localEvents.dateToDefine', 'Date à définir')}
        </span>
      );
    }
    return (
      <span className="local-event-date-badge local-event-date-known">
        <FiCalendar size={12} /> {event.dateText}
      </span>
    );
  };

  // Badge pour la confiance
  const renderConfidenceBadge = (event) => {
    const level = event.confidenceLevel || 'MEDIUM';
    const className = `local-event-confidence local-event-confidence-${level.toLowerCase()}`;
    
    if (level === 'HIGH') {
      return <span className={className}>✓ {t('localEvents.verified', 'Vérifié')}</span>;
    }
    return null; // Ne pas afficher pour MEDIUM/LOW
  };

  // Séparer et trier les événements
  const eventsWithDate = events.filter(e => e.dateStatus === 'KNOWN' && e.dateText);
  const eventsWithoutDate = events.filter(e => e.dateStatus !== 'KNOWN' || !e.dateText);
  
  // Trier par date (plus récent en premier)
  const sortedEventsWithDate = [...eventsWithDate].sort((a, b) => {
    const dateA = parseDateForSort(a.dateText);
    const dateB = parseDateForSort(b.dateText);
    return dateB - dateA; // Plus récent en premier
  });

  return (
    <div className="local-events-modal">
      {/* Header simplifié */}
      <div className="local-events-header">
        <button className="local-events-close" onClick={onBack || onClose}>
          ✕
        </button>
        <p className="local-events-subtitle">
          {events.length} {t('localEvents.found', 'événement(s) trouvé(s)')}
          {sources.length > 0 && (
            <span className="local-events-sources">
              {' '} • Sources: {sources.slice(0, 3).join(', ')}
            </span>
          )}
        </p>
      </div>

      {/* Liste des événements */}
      <div className="local-events-list">
        {events.length === 0 ? (
          <div className="local-events-empty">
            <p>{t('localEvents.noEvents', 'Aucun événement trouvé')}</p>
            <p className="local-events-empty-hint">
              {t('localEvents.tryDifferent', 'Essaie avec une autre ville ou période')}
            </p>
          </div>
        ) : (
          <>
            {/* Section 1: Événements avec date (triés par date récente) */}
            {sortedEventsWithDate.length > 0 && (
              <>
                {sortedEventsWithDate.map((event, index) => (
                  <div key={event.id || index} className="local-event-card">
                    {/* Header de la carte */}
                    <div className="local-event-card-header">
                      <h3 className="local-event-title">{event.title}</h3>
                      {renderConfidenceBadge(event)}
                    </div>

                    {/* Infos */}
                    <div className="local-event-info">
                      {renderDateBadge(event)}
                      
                      {(event.place || event.city) && (
                        <span className="local-event-location">
                          <FiMapPin size={12} />
                          {event.place ? `${event.place}, ${event.city}` : event.city}
                        </span>
                      )}
                    </div>

                    {/* Description (tronquée) */}
                    {event.description && (
                      <p className="local-event-description">
                        {event.description.length > 120 
                          ? `${event.description.substring(0, 120)}...` 
                          : event.description}
                      </p>
                    )}

                    {/* Footer: source + CTA */}
                    <div className="local-event-footer">
                      <a 
                        href={event.sourceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="local-event-source"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiExternalLink size={12} />
                        {event.sourceName || 'Voir la source'}
                      </a>
                      
                      <button 
                        className="local-event-cta"
                        onClick={() => handleCreateActivity(event)}
                      >
                        {t('localEvents.joinCta', "J'y vais")} →
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Section 2: Sources d'événements sans date précise */}
            {eventsWithoutDate.length > 0 && (
              <>
                <div className="local-events-section-header">
                  <h3>{t('localEvents.sourcesNearYou', 'Sources d\'événements près de chez toi')}</h3>
                  <p className="local-events-section-hint">
                    {t('localEvents.sourcesHint', 'Calendriers et agendas locaux à explorer')}
                  </p>
                </div>
                
                {eventsWithoutDate.map((event, index) => (
                  <div key={event.id || `no-date-${index}`} className="local-event-card local-event-card-no-date">
                    {/* Header de la carte */}
                    <div className="local-event-card-header">
                      <h3 className="local-event-title">{event.title}</h3>
                      {renderConfidenceBadge(event)}
                    </div>

                    {/* Infos */}
                    <div className="local-event-info">
                      {renderDateBadge(event)}
                      
                      {(event.place || event.city) && (
                        <span className="local-event-location">
                          <FiMapPin size={12} />
                          {event.place ? `${event.place}, ${event.city}` : event.city}
                        </span>
                      )}
                    </div>

                    {/* Description (tronquée) */}
                    {event.description && (
                      <p className="local-event-description">
                        {event.description.length > 120 
                          ? `${event.description.substring(0, 120)}...` 
                          : event.description}
                      </p>
                    )}

                    {/* Footer: source + CTA */}
                    <div className="local-event-footer">
                      <a 
                        href={event.sourceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="local-event-source"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiExternalLink size={12} />
                        {event.sourceName || 'Voir la source'}
                      </a>
                      
                      <button 
                        className="local-event-cta"
                        onClick={() => handleCreateActivity(event)}
                      >
                        {t('localEvents.joinCta', "J'y vais")} →
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// === HELPERS ===

/**
 * Mappe la catégorie d'un événement externe vers une catégorie Raskmote
 */
function mapEventCategoryToRaskmote(category) {
  if (!category) return 'Sortie';
  
  // Normalize: lowercase and remove accents
  const normalized = String(category)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  
  // Sport (French + English)
  const sportKeywords = [
    'sport', 'sports', 'course', 'running', 'trail', 'marathon',
    'football', 'soccer', 'tennis', 'natation', 'swim', 'swimming',
    'velo', 'bike', 'biking', 'cycling', 'fitness', 'gym', 'yoga'
  ];
  if (sportKeywords.some(k => normalized.includes(k))) {
    return 'Sport';
  }
  
  // Musique / Music (French + English) - checked first for priority
  const musicKeywords = [
    'musique', 'music', 'concert', 'festival', 'jazz', 'rock', 'pop', 'electro', 'electronic', 'dj'
  ];
  if (musicKeywords.some(k => normalized.includes(k))) {
    return 'Musique';
  }
  
  // Culture (French + English)
  const cultureKeywords = [
    'culture', 'cultural', 'exposition', 'exhibition', 'musee', 'museum',
    'theatre', 'theater', 'spectacle', 'show', 'art', 'cinema', 'movie', 'film'
  ];
  if (cultureKeywords.some(k => normalized.includes(k))) {
    return 'Culture';
  }
  
  return 'Sortie';
}

/**
 * Essaie de parser une date française en ISO
 */
function tryParseDate(dateText) {
  if (!dateText) return null;
  
  // Mapping mois français → numéro
  const months = {
    'janvier': '01', 'février': '02', 'mars': '03', 'avril': '04',
    'mai': '05', 'juin': '06', 'juillet': '07', 'août': '08',
    'septembre': '09', 'octobre': '10', 'novembre': '11', 'décembre': '12'
  };
  
  // Pattern: "7 mars 2026" ou "samedi 7 mars 2026"
  const match = dateText.match(/(\d{1,2})\s+(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\s*(\d{4})?/i);
  
  if (match) {
    const day = match[1].padStart(2, '0');
    const monthKey = match[2].toLowerCase();
    const month = months[monthKey];
    
    let year;
    if (match[3]) {
      // Year explicitly provided
      year = parseInt(match[3], 10);
    } else {
      // No year: choose next future occurrence
      const now = new Date();
      const currentYear = now.getFullYear();
      const monthIndex = parseInt(month, 10) - 1; // 0-based for Date
      const dayNumber = parseInt(day, 10);
      const candidateThisYear = new Date(currentYear, monthIndex, dayNumber, 10, 0, 0, 0);
      
      if (candidateThisYear < now) {
        year = currentYear + 1;
      } else {
        year = currentYear;
      }
    }
    
    // Retourner format ISO pour datetime-local
    return `${year}-${month}-${day}T10:00`;
  }
  
  return null;
}

/**
 * Parse une date pour le tri (retourne un timestamp)
 */
function parseDateForSort(dateText) {
  if (!dateText) return 0;
  
  const months = {
    'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
    'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
    'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
  };
  
  // Pattern: "7 mars 2026" ou "samedi 7 mars 2026"
  const match = dateText.match(/(\d{1,2})\s+(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\s*(\d{4})?/i);
  
  if (match) {
    const day = parseInt(match[1], 10);
    const month = months[match[2].toLowerCase()];
    const hasExplicitYear = !!match[3];
    const currentYear = new Date().getFullYear();
    let year = parseInt(match[3] || currentYear, 10);
    
    let parsedDate = new Date(year, month, day);
    
    // If no year provided and date is in the past, assume next year
    if (!hasExplicitYear) {
      const now = new Date();
      if (parsedDate.getTime() < now.getTime()) {
        parsedDate.setFullYear(parsedDate.getFullYear() + 1);
      }
    }
    
    return parsedDate.getTime();
  }
  
  // Pattern: "DD/MM/YYYY"
  const slashMatch = dateText.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slashMatch) {
    const day = parseInt(slashMatch[1], 10);
    const month = parseInt(slashMatch[2], 10) - 1;
    const year = parseInt(slashMatch[3], 10);
    return new Date(year, month, day).getTime();
  }
  
  return 0;
}

export default LocalEventsModal;


